import { loadStripe } from '@stripe/stripe-js';
import { API_ENDPOINTS } from '../constants';
import { locationUtils } from '../utils/locationUtils';

// Initialize Stripe
let stripePromise = null;
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
};

// Detect if user is from India
const isIndianCustomer = () => {
  // Check if user has explicitly selected India or if we can detect from IP
  const userCountry = localStorage.getItem('userCountry') || 'US';
  return userCountry === 'IN' || userCountry === 'India';
};

// Get country and currency based on payment gateway
const getCountryAndCurrency = (isIndia) => {
  const countryCode = isIndia ? 'IN' : 'US';
  return locationUtils.getCountryAndCurrencyForGateway(countryCode);
};

export const paymentService = {
  // Create payment intent/session
  createPaymentSession: async (planData, customerData) => {
    try {
      const isIndia = isIndianCustomer();
      const endpoint = isIndia 
        ? API_ENDPOINTS.PAYMENTS.CREATE_RAZORPAY_ORDER
        : API_ENDPOINTS.PAYMENTS.CREATE_STRIPE_SESSION;

      // Transform plan data to use plan_id instead of id
      const transformedPlanData = {
        plan_id: planData.plan_id,
        plan_name: planData.plan_name,
        price: planData.base_price, // Backend expects 'price' field
        base_price: planData.base_price,
        user_limit: planData.user_limit,
        ai_tokens_limit: planData.ai_tokens_limit,
        billing_cycle: planData.billing_cycle || 'Monthly'
      };

      const { country_code, currency_code, gateway } = getCountryAndCurrency(isIndia);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan_data: transformedPlanData,
          customer_data: customerData,
          payment_gateway: gateway,
          country_code,
          currency_code
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create payment session');
      }

      const result = await response.json();
      return {
        success: true,
        data: result.data,
        gateway
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Process Stripe payment
  processStripePayment: async (paymentMethodId, amount, currency = 'usd') => {
    try {
      await getStripe();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}${API_ENDPOINTS.PAYMENTS.CONFIRM_STRIPE_PAYMENT}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          payment_method_id: paymentMethodId,
          amount: amount,
          currency: currency
        }),
      });

      if (!response.ok) {
        throw new Error('Payment failed');
      }

      const result = await response.json();
      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Process Razorpay payment
  processRazorpayPayment: async (orderId, paymentId, signature) => {
    try {
      console.log('Verifying Razorpay payment:', { orderId, paymentId, signature });
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}${API_ENDPOINTS.PAYMENTS.VERIFY_RAZORPAY_PAYMENT}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_id: orderId,
          payment_id: paymentId,
          signature: signature
        }),
      });

      console.log('Verification response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Verification failed:', errorText);
        throw new Error(`Payment verification failed: ${response.status} ${errorText}`);
      }

      const result = await response.json();
      console.log('Verification result:', result);
      
      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      console.error('Payment verification error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Get payment status
  getPaymentStatus: async (paymentId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}${API_ENDPOINTS.PAYMENTS.CHECKOUT_STATUS}/${paymentId}`);
      
      if (!response.ok) {
        throw new Error('Failed to get payment status');
      }

      const result = await response.json();
      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Get payment history
  getPaymentHistory: async (clientId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}${API_ENDPOINTS.PAYMENTS.HISTORY}/${clientId}`);
      
      if (!response.ok) {
        throw new Error('Failed to get payment history');
      }

      const result = await response.json();
      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Set user country
  setUserCountry: (country) => {
    localStorage.setItem('userCountry', country);
  },

  // Get user country
  getUserCountry: () => {
    return localStorage.getItem('userCountry') || 'US';
  },

  // Detect user country from IP (optional)
  detectUserCountry: async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      const country = data.country_code;
      localStorage.setItem('userCountry', country);
      return country;
    } catch (error) {
      console.warn('Could not detect user country:', error);
      return 'US';
    }
  }
}; 